import { formatDate } from '@angular/common';
import { Component, HostBinding, inject, OnInit } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl } from '@ngneat/reactive-forms';
import { User } from 'src/app/classes/user.class';
import { CodaltComponent } from 'src/app/codalt.component';
import { PeriodType } from 'src/app/environment';
import { AuthorisationService } from 'src/app/services/auth/authorisation.service';
import { Functionalities } from 'src/app/services/auth/functionalities';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog-service/confirm-dialog.service';
import { PeriodService } from 'src/app/services/period.service';
import { SettlementsService } from 'src/app/services/settlements.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalStorage } from 'src/app/storage.class';
import { environment } from '../../../environments/environment';
import { Features } from '../../services/auth/features';

type ScreenContentType = 'periodeverrekening' | 'boekingsregels';

@Component({
    selector: 'app-settlement',
    templateUrl: './settlement.component.html',
    styleUrls: ['./settlement.component.scss'],
    standalone: false,
})
export class SettlementComponent extends CodaltComponent implements OnInit {
    @HostBinding('class.print') print = false;
    readonly = false;

    beginDate: Date;
    endDate: Date;
    nextMonth: Date | undefined;
    employeeWorkingHours: number;
    today = new Date();

    year: number;
    period: number;
    periodType = environment.periodType;

    fcUserId = new FormControl<number | undefined>();

    allEmployees: User[];
    employees: User[];
    user: User | undefined;

    screenContent: ScreenContentType;
    downloadingPdf = false;

    daytypeCounts: { type: string; count: number; minutes: number }[] = [];

    settling = false;

    private readonly periodService = inject(PeriodService);

    constructor(
        private userService: UserService,
        private settlementsService: SettlementsService,
        private activatedRoute: ActivatedRoute,
        private confirmDialogService: ConfirmDialogService,
        private router: Router,
        private title: Title
    ) {
        super();

        this.title.setTitle('Periodeverrekening' + environment.titleAppend);
    }

    ngOnInit(): void {
        const queryParams = this.activatedRoute.snapshot.queryParams as { print: string };

        this.print = !!queryParams?.print;

        this.readonly = !AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution) || this.print;

        if (!this.readonly) {
            this.subscriptions.add(
                this.fcUserId.valueChanges.subscribe(userId => {
                    this.router.navigateByUrl(
                        `${this.screenContent}/${userId}/${this.period}/${this.year}${this.print ? '?print=true' : ''}`
                    );
                })
            );
        }

        if (!AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution)) {
            this.user = LocalStorage.getUser();

            this.fcUserId.setValue(this.user.id);
        }

        this.subscriptions.add(
            this.activatedRoute.params.subscribe(params => {
                this.screenContent = this.activatedRoute.snapshot.url[0]?.path as ScreenContentType;

                const { beginDate, endDate, year, period, nextMonth } = this.periodService.getPeriodDateInfo({
                    period: +params.period,
                    year: +params.year,
                });

                this.period = period;
                this.year = year;
                this.beginDate = beginDate;
                this.endDate = endDate;
                this.nextMonth = nextMonth;

                if (this.allEmployees) {
                    this.filterAndSortEmployees();
                }

                if (
                    params?.userId &&
                    AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution)
                ) {
                    this.user = this.employees?.find(e => e.id === +params.userId);

                    this.fcUserId.setValue(+params.userId);
                }

                if (this.user) {
                    this.employeeWorkingHours = this.periodService.getEmployeeContractHoursPerPeriod(this.user, {
                        beginDate: this.beginDate,
                        endDate: this.endDate,
                    });
                }
            })
        );
        if (AuthorisationService.hasFeature(Features.Users)) {
            this.subscriptions.add(
                this.userService.getList(true, false, false, true).subscribe(users => {
                    this.allEmployees = users.filter(u => !!u.afas_employee_id);

                    this.filterAndSortEmployees();

                    if (!this.fcUserId.value) {
                        this.fcUserId.setValue(this.employees[0].id);
                    } else if (!this.user) {
                        this.user = this.employees?.find(e => e.id === +this.fcUserId.value);

                        // Fallback when selected user not in list (eg. Ian Example PDF)
                        if (!this.user) {
                            this.subscriptions.add(
                                this.userService.getApiList(true, true, true, false, true).subscribe(allusers => {
                                    this.user = allusers.find(u => u.id === +this.fcUserId.value);

                                    this.employees.push(this.user);
                                })
                            );
                        }

                        this.employeeWorkingHours = this.periodService.getEmployeeContractHoursPerPeriod(this.user, {
                            beginDate: this.beginDate,
                            endDate: this.endDate,
                        });
                    }
                })
            );
        }
    }

    settlingBackActionCheck(): Promise<boolean> {
        return new Promise(resolve => {
            this.confirmDialogService
                .confirm({
                    title: 'Bezig met verlonen',
                    text: `Bezig met verlonen van deze gebruiker`,
                    confirm: 'Sluiten',
                })
                .then(
                    () => {
                        resolve(false);
                    },
                    () => {
                        resolve(false);
                    }
                );
        });
    }

    isSettling(settle: boolean) {
        this.settling = settle;
    }

    filterAndSortEmployees() {
        const sortString = (z: User) => (z.group ?? 'ZZZZ') + (z.lastname ?? '') + (z.firstname ?? '');
        this.employees = this.allEmployees
            .filter(u => !u.deleted_at || new Date(u.deleted_at).getTime() > this.beginDate.getTime())
            .filter(u => !u.enddate || new Date(u.enddate).getTime() >= this.beginDate.getTime())
            .sort((a, b) => sortString(a).localeCompare(sortString(b)));
    }

    pdf() {
        this.downloadingPdf = true;

        if (this.readonly) {
            this.settlementsService.periodSettlementSinglePdf(this.year, this.period, this.user.id).subscribe({
                next: result => {
                    this.confirmDialogService.confirm({
                        title: 'Exporteren als PDF',
                        text: result.message,
                        confirm: 'Sluiten',
                    });

                    this.downloadingPdf = false;
                },
                error: () => {
                    this.downloadingPdf = false;
                },
            });
        } else {
            this.confirmDialogService
                .confirm({
                    title: 'Kies een export',
                    text: `Wil je alle periodeverrekening per e-mail of alleen de verrekening van ${this.user.name}?`,
                    confirm: `Alleen ${this.user.firstname ?? this.user.name}`,
                    cancel: 'Iedereen',
                })
                .then(
                    () => {
                        this.settlementsService
                            .periodSettlementSinglePdf(this.year, this.period, this.user.id)
                            .subscribe({
                                next: result => {
                                    this.confirmDialogService.confirm({
                                        title: 'Exporteren als PDF',
                                        text: result.message,
                                        confirm: 'Sluiten',
                                    });

                                    this.downloadingPdf = false;
                                },
                                error: () => {
                                    this.downloadingPdf = false;
                                },
                            });
                    },
                    () => {
                        this.settlementsService.periodSettlementAllPdf(this.year, this.period).subscribe({
                            next: result => {
                                this.confirmDialogService.confirm({
                                    title: 'Exporteren als PDF',
                                    text: result.message,
                                    confirm: 'Sluiten',
                                });

                                this.downloadingPdf = false;
                            },
                            error: () => {
                                this.downloadingPdf = false;
                            },
                        });
                    }
                );
        }
    }

    customSearchFn(term: string, item: User) {
        return (
            item.name.toLowerCase().includes(term.toLowerCase()) ||
            item.afas_employee_id?.padStart(4, '0').includes(term) ||
            item.group.toLowerCase().includes(term.toLowerCase())
        );
    }

    prevEmployee() {
        const index = this.employees.findIndex(e => e.id === this.fcUserId.value);
        if (index) {
            this.router.navigateByUrl(
                `${this.screenContent}/${this.employees[index - 1].id}/${this.period}/${this.year}`
            );
        }
    }

    nextEmployee() {
        const index = this.employees.findIndex(e => e.id === this.fcUserId.value);
        if (this.employees[index + 1] !== undefined) {
            this.router.navigateByUrl(
                `${this.screenContent}/${this.employees[index + 1].id}/${this.period}/${this.year}`
            );
        }
    }

    next() {
        let period = this.period + 1;
        let year = this.year;

        if (period > 13) {
            period = 1;
            year += 1;
        }

        if (!AuthorisationService.hasFunctionality(Functionalities.PeriodSettlementExecution)) {
            let today = new Date();

            today.setDate(today.getDate() - 56);

            let currentWeek = +formatDate(today, 'w', 'nl');

            if (today.getDay() === 0) {
                currentWeek -= 1;
            }

            currentWeek--;

            if (period > Math.floor(currentWeek / 4 + 1) || year > today.getFullYear()) {
                return;
            }
        }

        this.router.navigateByUrl(`${this.screenContent}/${this.user.id}/${period}/${year}`);
    }

    prev() {
        let period = this.period - 1;
        let year = this.year;

        if (period < 1) {
            period = this.periodType === PeriodType.Month ? 12 : 13;
            year -= 1;
        }

        this.router.navigateByUrl(`${this.screenContent}/${this.user.id}/${period}/${year}`);
    }
}
